// What a Data Lab tool knows about itself: the name on its badge, the glyph the page registered
// for its code, and the noun its update action is phrased with.
//
// Both halves used to be computed in the block shells and handed over as a prop, which meant the
// icon map made a round trip -- the React render shell publishes it, the shell read it back off the
// global, and passed it to React again. The block knows its own type, so it resolves its own badge.

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// Keyed by tool code; the mapping variants are the same tools sending their data to a portal.
const TOOL_NAMES = {
  dlv: 'Virtual question',
  dlc: 'Correlation finder',
  dlx: 'Xpert question',
  dld: 'Concept Lab',
  'dld-mapping': 'Concept Mapping',
  dlt: 'Tracker Lab',
  'dlt-mapping': 'Tracker Mapping'
}

/** The code a tool is registered under, which gains a suffix once it is sending to a portal. */
export const toolIconCode = (type, insightsMapping = false) => `${type}${insightsMapping ? '-mapping' : ''}`

/**
 * The badge for a tool, or null where the type names no tool — the caller draws nothing then,
 * which is what a block with no badge did before. The lookup is an own-property one: the type
 * arrives from the question model, so an inherited member would otherwise resolve to something
 * truthy and be rendered.
 */
export const resolveToolIcon = (type, insightsMapping = false) => {
  if (!type) return null

  const code = toolIconCode(type, insightsMapping)
  if (!Object.prototype.hasOwnProperty.call(TOOL_NAMES, code)) return null

  const glyph = PageGlobals.QUESTION_TYPE_ICONS[code]
  return { words: TOOL_NAMES[code].split(' '), className: glyph ? `icon-aytmfd-${glyph}` : null }
}

// The noun the update action is phrased with -- "Generate visualization", "Update comparison".
// Both mapping tools and their portal variants share one word.
const TOOL_UPDATE_NOUNS = {
  dlv: 'visualization',
  dlc: 'data',
  dlx: 'questions',
  dld: 'comparison',
  'dld-mapping': 'comparison',
  dlt: 'comparison',
  'dlt-mapping': 'comparison'
}

/** The noun for a tool, or an empty string where the type names none, so the label stays readable. */
export const resolveToolUpdateNoun = (type, insightsMapping = false) => {
  if (!type) return ''

  const code = toolIconCode(type, insightsMapping)
  return Object.prototype.hasOwnProperty.call(TOOL_UPDATE_NOUNS, code) ? TOOL_UPDATE_NOUNS[code] : ''
}
